<script setup lang="ts">
    import { createTextVNode, h, onMounted, ref } from 'vue';
    import type { VNode, VNodeArrayChildren, VNodeChild } from 'vue';
    import type { StoryblokRichTextNode } from '@storyblok/richtext';
    import { useRichTextRenderer } from '../../composables/useRichTextRenderer';

    type Props = {
        doc?: StoryblokRichtext | null;
        vars?: Record<string, string | number | null | undefined>;
    };

    const props = defineProps<Props>();

    const { displayFirstName } = useLoginState();

    const USER_PLACEHOLDER = '__MEDICE_USER_PLACEHOLDER__';
    const isHydrated = ref(false);

    onMounted(() => {
        isHydrated.value = true;
    });

    const mergedVars = computed(() => {
        const baseVars = {
            ...(props.vars || {})
        };

        if (!isHydrated.value) {
            return {
                ...baseVars,
                user: USER_PLACEHOLDER
            };
        }

        return {
            ...baseVars,
            user: displayFirstName.value
        };
    });

    const { render } = useRichTextRenderer();

    const buildSkeleton = (): VNode =>
        h('span', {
            class: 'loading-skeleton --line rt-user-skeleton',
            'aria-hidden': 'true'
        });

    const replaceUserPlaceholder = (text: string): VNodeArrayChildren => {
        if (!text.includes(USER_PLACEHOLDER)) return [createTextVNode(text)];
        const parts = text.split(USER_PLACEHOLDER);
        const nodes: VNodeArrayChildren = [];

        parts.forEach((part, index) => {
            if (part) nodes.push(createTextVNode(part));
            if (index < parts.length - 1) nodes.push(buildSkeleton());
        });

        return nodes;
    };

    const content = computed(() => {
        if (!props.doc) return null;
        return render(props.doc, mergedVars.value, {
            transformHref: (href: string) => useStoryblokUrl(href),
            textResolver: (text) => replaceUserPlaceholder(text)
        }) as unknown as VNodeChild;
    });

    const RichTextRender = defineComponent({
        name: 'RichTextRender',
        inheritAttrs: false,
        setup() {
            return () => content.value;
        }
    });
</script>

<template>
    <RichTextRender v-if="content" />
</template>

<style scoped lang="scss">
    @import '@/assets/scss/basics/loading-skeleton.scss';

    h1 {
        font-size: var(--font-xl);
        margin-bottom: 32px;
    }

    h2 {
        font-size: var(--font-l);
    }

    h3 {
        font-size: var(--font-m);
        font-weight: 600;
    }

    h4 {
        font-size: var(--font-sm);
    }

    a {
        color: var(--link-color);
        text-decoration: underline;
        font-weight: 600;
    }

    strong {
        font-weight: 700;
        span {
            font-weight: inherit;
        }
    }

    em {
        font-style: italic;
        span {
            font-style: inherit;
        }
    }

    ul {
        list-style: disc;
        padding-inline-start: var(--spacing--2);
        li {
            list-style: disc;
        }
    }

    ol {
        list-style: decimal;
        padding-inline-start: var(--spacing--2);
        li {
            list-style: decimal;
        }
    }
</style>
